import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import { listCredentialsQueue } from "../../api/clientVendorDirectoryVerificationApi";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.credentials";

const STATUS_FILTERS = [
  "",
  "submitted",
  "pending_review",
  "verified",
  "rejected",
  "expired",
  "revoked",
  "superseded",
];

const PENDING_STATUSES = new Set(["submitted", "pending_review"]);

function statusBadgeClass(status) {
  if (status === "verified") return "cvd-badge--published";
  if (["rejected", "revoked", "expired"].includes(status)) return "cvd-badge--suspended";
  if (["pending_review", "submitted"].includes(status)) return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function formatDateTime(value, locale) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

function credentialTypeLabel(item) {
  return item.credentialTypeName || item.credentialTypeKey || item.credentialTypeId || "—";
}

function isExpired(item) {
  if (!item?.expiresAt) return false;
  return new Date(item.expiresAt) < new Date();
}

function isExpiringSoon(item, withinDays = 30) {
  if (!item?.expiresAt || isExpired(item)) return false;
  const limit = new Date();
  limit.setDate(limit.getDate() + withinDays);
  return new Date(item.expiresAt) <= limit;
}

function MetricCard({ label, value }) {
  return (
    <article className="cvd-metric-card">
      <span className="cvd-metric-card__label">{label}</span>
      <strong className="cvd-metric-card__value">{value ?? 0}</strong>
    </article>
  );
}

function CredentialStatusBadge({ status, t }) {
  return (
    <span className={`cvd-badge ${statusBadgeClass(status)}`}>
      {t(`${COPY_PREFIX}.status.${status}`, {}, status)}
    </span>
  );
}

export default function ClientVendorDirectoryCredentialListPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { activeView } = useOutletContext() || {};
  const { t, locale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const status = searchParams.get("status") || "";
  const expiring = searchParams.get("expiring") === "true";
  const basePath = `${serviciosBase}/credenciales`;
  const viewName = activeView?.name || "CasaMapa";

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await listCredentialsQueue(clientViewId, {
        status: status || undefined,
        expiringWithinDays: expiring ? 30 : undefined,
      });
      setCredentials(body.credentials || []);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
      setCredentials([]);
    } finally {
      setLoading(false);
    }
  }, [clientViewId, expiring, status, t]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    const pending = credentials.filter((item) => PENDING_STATUSES.has(item.credentialStatus)).length;
    const verified = credentials.filter((item) => item.credentialStatus === "verified").length;
    const expiringCount = credentials.filter((item) => isExpiringSoon(item)).length;
    return { total: credentials.length, pending, verified, expiring: expiringCount };
  }, [credentials]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const toggleExpiring = () => {
    updateParam("expiring", expiring ? "" : "true");
  };

  return (
    <div className="page-content cvd-admin cvd-credentials">
      <div className="cvd-admin__header">
        <div>
          <h1 className="cvd-admin__title">{t(`${COPY_PREFIX}.title`)}</h1>
          <p className="lede">{t(`${COPY_PREFIX}.intro`, { viewName })}</p>
        </div>
        <div className="cvd-admin__actions">
          <button type="button" className="cvd-btn cvd-btn--ghost" onClick={load}>
            {t(`${COPY_PREFIX}.refresh`)}
          </button>
        </div>
      </div>

      {!loading && credentials.length > 0 ? (
        <div className="cvd-metric-grid">
          <MetricCard label={t(`${COPY_PREFIX}.kpi.total`)} value={summary.total} />
          <MetricCard label={t(`${COPY_PREFIX}.kpi.pending`)} value={summary.pending} />
          <MetricCard label={t(`${COPY_PREFIX}.kpi.verified`)} value={summary.verified} />
          <MetricCard label={t(`${COPY_PREFIX}.kpi.expiring`)} value={summary.expiring} />
        </div>
      ) : null}

      <div className="cvd-duplicate-filters">
        <div className="cvd-duplicate-filters__group">
          <span className="cvd-duplicate-filters__label">{t(`${COPY_PREFIX}.filterStatus`)}</span>
          <div className="cvd-run-filters" role="group" aria-label={t(`${COPY_PREFIX}.filterStatus`)}>
            {STATUS_FILTERS.map((value) => (
              <button
                key={value || "all-status"}
                type="button"
                className={`cvd-run-filter-chip${status === value ? " is-active" : ""}`}
                onClick={() => updateParam("status", value)}
              >
                {value ? t(`${COPY_PREFIX}.status.${value}`) : t(`${COPY_PREFIX}.allStatuses`)}
              </button>
            ))}
          </div>
        </div>

        <div className="cvd-duplicate-filters__group">
          <div className="cvd-run-filters">
            <button
              type="button"
              className={`cvd-run-filter-chip${expiring ? " is-active" : ""}`}
              onClick={toggleExpiring}
            >
              {t(`${COPY_PREFIX}.filterExpiring`)}
            </button>
          </div>
        </div>
      </div>

      {loading ? <ClientPortalLoading message={t(`${COPY_PREFIX}.loading`)} /> : null}
      {!loading && errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}

      {!loading && !errorMessage && credentials.length === 0 ? (
        <section className="cvd-run-empty">
          <h2>{t(`${COPY_PREFIX}.emptyTitle`)}</h2>
          <p>{t(`${COPY_PREFIX}.emptyHint`)}</p>
        </section>
      ) : null}

      {!loading && !errorMessage && credentials.length > 0 ? (
        <div className="cvd-run-list">
          {credentials.map((item) => (
            <article key={item.id} className="cvd-run-card cvd-credential-card">
              <div className="cvd-run-card__main">
                <div className="cvd-run-card__head">
                  <div>
                    <h2 className="cvd-credential-card__vendor">{item.vendorName || item.vendorId}</h2>
                    <p className="cvd-run-card__sub">
                      {credentialTypeLabel(item)}
                      {item.credentialNumber ? ` · ${item.credentialNumber}` : ""}
                    </p>
                  </div>
                  <div className="cvd-credential-card__badges">
                    <CredentialStatusBadge status={item.credentialStatus} t={t} />
                    {item.isPublic ? (
                      <span className="cvd-badge cvd-badge--draft">{t(`${COPY_PREFIX}.publicBadge`)}</span>
                    ) : null}
                    {isExpired(item) ? (
                      <span className="cvd-badge cvd-badge--suspended">{t(`${COPY_PREFIX}.expiredBadge`)}</span>
                    ) : isExpiringSoon(item) ? (
                      <span className="cvd-badge cvd-badge--warning">{t(`${COPY_PREFIX}.expiringBadge`)}</span>
                    ) : null}
                  </div>
                </div>

                <div className="cvd-run-card__stats">
                  <span>
                    {t(`${COPY_PREFIX}.table.authority`)}: {item.issuingAuthority || "—"}
                  </span>
                  <span>
                    {t(`${COPY_PREFIX}.table.expires`)}: <strong>{formatDateTime(item.expiresAt, locale)}</strong>
                  </span>
                  <span>
                    {t(`${COPY_PREFIX}.table.updated`)}: {formatDateTime(item.updatedAt, locale)}
                  </span>
                </div>
              </div>

              <div className="cvd-run-card__actions">
                <Link className="cvd-btn cvd-btn--primary" to={`${basePath}/${item.id}`}>
                  {t(`${COPY_PREFIX}.review`)}
                </Link>
              </div>
            </article>
          ))}
        </div>
      ) : null}
    </div>
  );
}
